import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlay, FaShoppingCart, FaUserCircle } from "react-icons/fa";
import { isTestUnlocked, hasActiveSubscription, isSubscriptionExpired, getPlaySubscription, getTestValidityExpiry, getLatestTestValidityExpiry } from "../../utils/subscriptionHelper";
import EngdemyLogo from "../../assets/Images/EngdemyUploaded.png";
import "../../pages/Dashboard.css";
import "./dashboardpte.css";

const getStoredUser = (reduxUser) => {
    try {
        if (reduxUser?.result) return reduxUser.result;
        if (reduxUser?.email || reduxUser?._id) return reduxUser;
        const raw = localStorage.getItem("user_info");
        if (raw) {
            const localUser = JSON.parse(raw);
            if (localUser?.result) return localUser.result;
            if (localUser?.email || localUser?._id) return localUser;
        }
        const authRaw = localStorage.getItem("authData");
        if (authRaw) {
            const localAuth = JSON.parse(authRaw);
            if (localAuth?.result) return localAuth.result;
            if (localAuth?.email || localAuth?._id) return localAuth;
        }
    } catch (e) {
        // ignore
    }
    return null;
};

const DashboardPTE = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth?.authData);

    const [currentUser, setCurrentUser] = useState(() => getStoredUser(reduxUser));
    const [testFilter, setTestFilter] = useState("all");
    const [activeTab, setActiveTab] = useState("tests"); // 'tests' | 'calculator'
    const [calcPteScore, setCalcPteScore] = useState("75");

    useEffect(() => {
        const storedUser = getStoredUser(reduxUser);
        if (storedUser) {
            setCurrentUser(storedUser);
        }

        if (!reduxUser && storedUser) {
            const raw = localStorage.getItem("user_info");
            let dataToDispatch = null;
            try {
                const parsed = raw ? JSON.parse(raw) : null;
                if (parsed?.result) dataToDispatch = parsed;
            } catch (e) {}
            if (!dataToDispatch) {
                dataToDispatch = { result: storedUser, ...storedUser };
            }
            dispatch({ type: "AUTH", data: dataToDispatch });
        }

        if (storedUser && Array.isArray(storedUser.paidTests)) {
            localStorage.setItem("paidTests", JSON.stringify(storedUser.paidTests));
            const isExpired = isSubscriptionExpired(storedUser);
            const hasPaid = storedUser.paidTests.length > 0;
            const hasSub = hasActiveSubscription(storedUser);
            if (isExpired || (!hasPaid && !hasSub)) {
                localStorage.removeItem("isPaidUser");
            }
        }
    }, [reduxUser, dispatch]);

    const playSubscription = getPlaySubscription();
    const rawPaid = (currentUser && Array.isArray(currentUser.paidTests))
        ? currentUser.paidTests
        : JSON.parse(localStorage.getItem("paidTests") || "[]");
    const paidTests = Array.isArray(rawPaid) ? rawPaid : [];

    // Subscription Given Date & Remaining Days
    const getSubscriptionDetails = () => {
        const testValidityExpiry = getLatestTestValidityExpiry(currentUser?.testValidity);
        const rawExpiry = currentUser?.subscriptionExpiresAt || playSubscription?.expiresAt || testValidityExpiry;
        const hasSub = hasActiveSubscription(currentUser);
        const hasPaidTests = (currentUser && Array.isArray(currentUser.paidTests))
            ? currentUser.paidTests.length > 0
            : (Array.isArray(paidTests) ? paidTests.length > 0 : paidTests === "all");

        if (!rawExpiry && !hasSub && (!currentUser?.packageName && !hasPaidTests && localStorage.getItem("isPaidUser") !== "true")) {
            return {
                givenDate: "N/A",
                daysLeft: 0,
                daysLeftText: "Free Test",
                expiryText: "Free Test",
                isExpired: false
            };
        }

        const rawGiven = currentUser?.subscriptionStartedAt || currentUser?.createdAt || playSubscription?.startedAt;
        let formattedGiven = "N/A";
        try {
            if (rawGiven) {
                const d = new Date(rawGiven);
                if (!isNaN(d.getTime())) {
                    formattedGiven = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                }
            }
        } catch (e) {
            formattedGiven = "N/A";
        }

        let daysLeft = 0;
        let formattedExpiry = "N/A";
        let isExpired = false;
        try {
            let exp = rawExpiry ? new Date(rawExpiry) : null;
            if ((!exp || isNaN(exp.getTime())) && (currentUser?.packageName || localStorage.getItem("isPaidUser") === "true")) {
                const start = new Date(rawGiven || Date.now());
                exp = new Date(start.getTime() + 15 * 24 * 60 * 60 * 1000);
            }
            if (exp && !isNaN(exp.getTime())) {
                const now = new Date();
                const diff = exp.getTime() - now.getTime();
                daysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                formattedExpiry = exp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                isExpired = diff <= 0;
            }
        } catch (e) {
            daysLeft = 0;
            isExpired = true;
        }

        if (!isExpired && isSubscriptionExpired(currentUser)) {
            isExpired = true;
            daysLeft = 0;
        }

        return {
            givenDate: formattedGiven,
            daysLeft: daysLeft,
            daysLeftText: daysLeft > 0 ? `${daysLeft} Days Left` : "Expired",
            expiryText: formattedExpiry,
            isExpired: isExpired || (rawExpiry && new Date(rawExpiry) <= new Date())
        };
    };

    const subDetails = getSubscriptionDetails();
    const isUserSubscribed = !subDetails.isExpired && (hasActiveSubscription(currentUser) || subDetails.daysLeft > 0);
    const hasUserEverPurchased = Boolean(
        currentUser?.subscriptionExpiresAt ||
        currentUser?.subscriptionStartedAt ||
        currentUser?.packageName ||
        (currentUser?.paidTests && currentUser.paidTests.length > 0) ||
        (currentUser?.testValidity && Object.keys(currentUser.testValidity).length > 0) ||
        playSubscription?.expiresAt ||
        playSubscription?.productId ||
        (typeof window !== "undefined" && localStorage.getItem("isPaidUser") === "true")
    );

    const isTestActive = (testNum) => {
        // If access/subscription or validity is expired, paid tests (3-50) CANNOT be started (no start test should be there)
        if (subDetails.isExpired || isSubscriptionExpired(currentUser)) return false;

        // Check specific test expiry from testValidity
        if (currentUser?.testValidity) {
            const exp = getTestValidityExpiry(currentUser.testValidity, testNum);
            if (exp && exp <= new Date()) {
                return false;
            }
        }

        // Tests 1-2 always free (available if validity is not expired)
        if (testNum <= 2) return true;

        // Active verified subscription unlocks all tests
        if (isUserSubscribed) return true;

        // If user has zero paid tests, paid tests (3-50) CANNOT be started - no start test should be there
        if (!paidTests || (Array.isArray(paidTests) && paidTests.length === 0)) {
            return false;
        }

        if (paidTests === "all") return true;
        if (paidTests && Array.isArray(paidTests) && (paidTests.includes(testNum) || paidTests.includes(Number(testNum)))) return true;
        return isTestUnlocked(testNum, currentUser);
    };

    // 50 Full Mock Tests for PTE Core
    const mockTests = Array.from({ length: 50 }, (_, i) => ({
        id: i + 1,
        isLocked: !isTestActive(i + 1),
    }));

    const filteredMockTests = mockTests.filter((test) => {
        if (testFilter === 'unlocked') return !test.isLocked;
        if (testFilter === 'locked') return test.isLocked;
        return true;
    });

    const unlockedCount = mockTests.filter(t => !t.isLocked).length;
    const lockedCount = mockTests.filter(t => t.isLocked).length;

    const handleTestStart = (testId) => {
        if (isTestActive(testId)) {
            navigate(`/pte/fulltest/${testId}`);
        } else {
            navigate("/pricing?category=tests");
        }
    };

    const handleBuyNow = () => navigate("/pricing?category=tests");

    // PTE Core to CLB Conversion
    const getPteClbLevel = (score) => {
        const s = Number(score);
        if (isNaN(s) || s < 10) return "—";
        if (s >= 89) return "10";
        if (s >= 82) return "9";
        if (s >= 76) return "8";
        if (s >= 68) return "7";
        if (s >= 59) return "6";
        if (s >= 51) return "5";
        if (s >= 42) return "4";
        return "Below CLB 4";
    };

    return (
        <div className="paragon-portal-wrapper pte-theme">

            {/* ── TOP OFFICIAL HEADER BAR WITH ENGDEMY LOGO ── */}
            <div className="paragon-header">
                <div className="paragon-logo-wrap" onClick={() => setActiveTab('tests')}>
                    <img src={EngdemyLogo} alt="Engdemy" className="paragon-engdemy-logo" />
                </div>

                <div className="paragon-user-nav">
                    <div className="paragon-cart-icon" title="Shopping Cart / Pricing" onClick={() => navigate("/pricing?category=tests")}>
                        <FaShoppingCart />
                    </div>
                    <div 
                        className="paragon-user-badge"
                        onClick={() => navigate(currentUser ? "/profile" : "/login")}
                        style={{ cursor: "pointer" }}
                        title={currentUser?.email ? `Logged in as ${currentUser.email}` : "Click to log in"}
                    >
                        <FaUserCircle className="paragon-user-avatar" />
                        <span className="paragon-user-email">
                            {currentUser?.email || currentUser?.name || (currentUser ? "My Account" : "Sign In")}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── SECONDARY NAVIGATION TABS (Test 1-50, Score Calculator, Progress) ── */}
            <div className="paragon-tabs-bar">
                <button
                    className={`paragon-tab-btn ${activeTab === 'tests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tests')}
                >
                    Test 1–50
                </button>
                <button
                    className={`paragon-tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
                    onClick={() => setActiveTab('calculator')}
                >
                    PTE Score Calculator
                </button>
            </div>

            {/* ── MAIN CONTENT CONTAINER ── */}
            <div className="paragon-content-container">

                {/* TAB 1: PRACTICE TESTS TABLE */}
                {activeTab === 'tests' && (
                    <div>
                        {/* Section Subheader with Filter Buttons */}
                        <div className="paragon-section-header" style={{ alignItems: 'flex-start' }}>
                            <div>
                                <h1 className="paragon-section-title" style={{ fontSize: '24px', fontWeight: '900', marginBottom: '4px' }}>
                                    PTE Core Mock Test Dashboard
                                </h1>
                                <p style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', margin: 0 }}>
                                    My Study Materials • 50 Full-Length Computer-Delivered Tests with AI Scoring
                                </p>
                            </div>
                            <div className="paragon-filter-group" style={{ alignSelf: 'center' }}>
                                <button
                                    className={`paragon-filter-btn ${testFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setTestFilter('all')}
                                >
                                    All (50)
                                </button>
                                <button
                                    className={`paragon-filter-btn ${testFilter === 'unlocked' ? 'active' : ''}`}
                                    onClick={() => setTestFilter('unlocked')}
                                >
                                    Active / Free ({unlockedCount})
                                </button>
                                <button
                                    className={`paragon-filter-btn ${testFilter === 'locked' ? 'active' : ''}`}
                                    onClick={() => setTestFilter('locked')}
                                >
                                    {hasUserEverPurchased ? 'Expired' : 'Locked'} ({lockedCount})
                                </button>
                            </div>
                        </div>

                        {/* Subscription Summary Banner */}
                        {isUserSubscribed ? (
                            <div className="paragon-sub-summary-banner">
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Subscription Given:</span>
                                    <strong className="paragon-sub-summary-value">{subDetails.givenDate}</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Valid Until:</span>
                                    <strong className="paragon-sub-summary-value">{subDetails.expiryText}</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-days-left-pill prominent">{subDetails.daysLeftText}</span>
                                </div>
                            </div>
                        ) : hasUserEverPurchased ? (
                            <div className="paragon-sub-summary-banner">
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Status:</span>
                                    <strong className="paragon-sub-summary-value" style={{ color: '#dc2626' }}>Subscription Expired</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-sub-summary-label">Expired On:</span>
                                    <strong className="paragon-sub-summary-value">{subDetails.expiryText}</strong>
                                </div>
                                <div className="paragon-sub-summary-divider">|</div>
                                <div className="paragon-sub-summary-item">
                                    <span className="paragon-days-left-pill prominent">Expired</span>
                                </div>
                            </div>
                        ) : (
                            <div className="paragon-sub-summary-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                    <div className="paragon-sub-summary-item">
                                        <span className="paragon-sub-summary-label">Access Level:</span>
                                        <strong className="paragon-sub-summary-value">Free Test (1 &amp; 2)</strong>
                                    </div>
                                    <div className="paragon-sub-summary-divider">|</div>
                                    <div className="paragon-sub-summary-item">
                                        <span className="paragon-sub-summary-label">Validity:</span>
                                        <strong className="paragon-sub-summary-value">Free Test</strong>
                                    </div>
                                    <div className="paragon-sub-summary-divider">|</div>
                                    <div className="paragon-sub-summary-item">
                                        <span className="paragon-days-left-pill prominent">Free Test</span>
                                    </div>
                                </div>
                                <button
                                    onClick={handleBuyNow}
                                    style={{
                                        padding: '8px 18px',
                                        borderRadius: '12px',
                                        backgroundColor: '#0d9488',
                                        color: '#ffffff',
                                        fontSize: '12px',
                                        fontWeight: '900',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        border: '1px solid #000',
                                        boxShadow: '2px 2px 0 0 #000',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    Unlock Tests 3–50
                                </button>
                            </div>
                        )}

                        {/* Practice Tests Table */}
                        <table className="paragon-tests-table">
                            <tbody>
                                {filteredMockTests.map((test) => {
                                    const isFreeOrActive = !test.isLocked;
                                    return (
                                        <tr key={test.id} className="paragon-test-row">
                                            {/* Status / Expiry Column */}
                                            <td className="paragon-cell-status">
                                                {test.id <= 2 && !isUserSubscribed ? (
                                                    <span className="paragon-free-status-tag">Free Test</span>
                                                ) : isFreeOrActive ? (
                                                    <div className="paragon-status-card">
                                                        <div className="paragon-given-text">
                                                            <span className="paragon-status-lbl">Given:</span> {subDetails.givenDate}
                                                        </div>
                                                        <div className="paragon-days-left-pill">
                                                            {subDetails.daysLeftText}
                                                        </div>
                                                        <div className="paragon-tz-text">
                                                            Expires: {subDetails.expiryText}
                                                        </div>
                                                    </div>
                                                ) : subDetails.isExpired ? (
                                                    <div className="paragon-status-card">
                                                        <div className="paragon-given-text">
                                                            <span className="paragon-status-lbl">Given:</span> {subDetails.givenDate}
                                                        </div>
                                                        <div className="paragon-days-left-pill prominent" style={{ background: '#fde8ec', color: '#dc2626', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
                                                            Expired
                                                        </div>
                                                        <div className="paragon-tz-text">
                                                            Expires: {subDetails.expiryText}
                                                        </div>
                                                    </div>
                                                ) : hasUserEverPurchased ? (
                                                    <span className="paragon-expired">Expired</span>
                                                ) : (
                                                    <span className="paragon-locked-tag">Locked</span>
                                                )}
                                            </td>

                                            {/* Product Title & Subtitle Column */}
                                            <td className="paragon-cell-info">
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                        <span
                                                            className="paragon-product-link"
                                                            onClick={() => {
                                                                if (isFreeOrActive) {
                                                                    handleTestStart(test.id);
                                                                } else {
                                                                    handleBuyNow();
                                                                }
                                                            }}
                                                        >
                                                            {test.id === 1
                                                                ? "Free PTE Core Practice Test 1"
                                                                : test.id === 2
                                                                ? "Free PTE Core Practice Test 2"
                                                                : `PTE Core Practice Test ${test.id}`}
                                                        </span>
                                                        {test.id <= 2 && (
                                                            <span className="paragon-free-badge">
                                                                Free Test
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="paragon-product-subtext">
                                                        Electronic Product (Online)
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Action Column */}
                                            <td className="paragon-cell-actions">
                                                {isFreeOrActive ? (
                                                    <div className="paragon-actions-group">
                                                        <button
                                                            className="paragon-start-btn"
                                                            onClick={() => handleTestStart(test.id)}
                                                            title={`Start Test ${test.id} Full Test`}
                                                        >
                                                            <FaPlay style={{ fontSize: 10 }} />
                                                            <span>Start Test</span>
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/pte/listeningtest/${test.id}`)}
                                                            title="Practice Listening"
                                                        >
                                                            L
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/pte/readingtest/${test.id}`)}
                                                            title="Practice Reading"
                                                        >
                                                            R
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/pte/writingtest/${test.id}`)}
                                                            title="Practice Writing"
                                                        >
                                                            W
                                                        </button>
                                                        <button
                                                            className="paragon-mod-btn"
                                                            onClick={() => navigate(`/pte/speakingtest/${test.id}`)}
                                                            title="Practice Speaking"
                                                        >
                                                            S
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="paragon-buy-btn"
                                                        onClick={handleBuyNow}
                                                    >
                                                        Buy Now
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 2: PTE SCORE CALCULATOR */}
                {activeTab === 'calculator' && (
                    <div className="paragon-calculator-section">
                        <div className="paragon-section-header">
                            <div>
                                <h2 className="paragon-section-title">PTE Core to CLB Score Calculator</h2>
                                <p style={{ fontSize: '15px', color: '#6b7280', margin: '6px 0 0' }}>
                                    Convert your PTE Core scaled score (10–90) to the official Canadian Language Benchmark (CLB) level.
                                </p>
                            </div>
                        </div>

                        <div className="paragon-calc-grid">
                            <div className="paragon-calc-card">
                                <div className="paragon-calc-card-header">
                                    <div className="paragon-calc-module-icon reading" style={{ background: '#fde8ec', color: '#800000' }}>⚡</div>
                                    <div>
                                        <h3 className="paragon-calc-card-title">PTE Core Scaled Score</h3>
                                        <div className="paragon-calc-card-sub">Range 10–90 Points</div>
                                    </div>
                                </div>

                                <div className="paragon-calc-input-row">
                                    <label className="paragon-calc-label">Enter Score (10–90):</label>
                                    <div className="paragon-calc-input-wrapper">
                                        <input
                                            type="number"
                                            min="10"
                                            max="90"
                                            value={calcPteScore}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '' || (Number(val) >= 10 && Number(val) <= 90)) {
                                                    setCalcPteScore(val);
                                                }
                                            }}
                                            placeholder="e.g. 78"
                                            className="paragon-calc-number-input"
                                        />
                                        <span className="paragon-calc-total-tag">/ 90</span>
                                    </div>
                                </div>

                                <input
                                    type="range"
                                    min="10"
                                    max="90"
                                    value={calcPteScore === '' ? 10 : Number(calcPteScore)}
                                    onChange={(e) => setCalcPteScore(e.target.value)}
                                    className="paragon-calc-range-slider"
                                />

                                <div className="paragon-calc-result-box reading" style={{ borderLeftColor: '#800000' }}>
                                    <div className="paragon-calc-result-label">Calculated CLB Level</div>
                                    <div className="paragon-calc-result-value" style={{ color: '#800000' }}>
                                        {calcPteScore === '' ? '—' : `CLB ${getPteClbLevel(calcPteScore)}`}
                                    </div>
                                    <div className="paragon-calc-result-note">
                                        {calcPteScore !== '' && Number(calcPteScore) >= 82 && (
                                            <span style={{ color: '#047857' }}>🌟 Excellent! Meets CLB 9+ requirement for maximum Express Entry CRS points.</span>
                                        )}
                                        {calcPteScore !== '' && Number(calcPteScore) >= 68 && Number(calcPteScore) < 82 && (
                                            <span style={{ color: '#b45309' }}>👍 Good standing! Meets CLB 7–8 standard PR requirements.</span>
                                        )}
                                        {calcPteScore !== '' && Number(calcPteScore) < 68 && (
                                            <span style={{ color: '#800000' }}>⚡ Needs practice. Recommend completing more practice tests.</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Official PTE Core to CLB Reference Table */}
                        <div className="paragon-calc-reference-card">
                            <h3 className="paragon-calc-ref-title">
                                📋 Official PTE Core to CLB Conversion Table
                            </h3>
                            <table className="paragon-calc-ref-table">
                                <thead>
                                    <tr>
                                        <th>CLB Level</th>
                                        <th>PTE Core Score Range</th>
                                        <th>Immigration Eligibility</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="highlight-row">
                                        <td><strong>CLB 10</strong></td>
                                        <td><strong>89–90</strong></td>
                                        <td>Maximum Express Entry Points</td>
                                    </tr>
                                    <tr className="highlight-row">
                                        <td><strong>CLB 9</strong></td>
                                        <td><strong>82–88</strong></td>
                                        <td>Federal Skilled Worker (FSW) Optimum</td>
                                    </tr>
                                    <tr>
                                        <td><strong>CLB 8</strong></td>
                                        <td><strong>76–81</strong></td>
                                        <td>Express Entry Strong Contender</td>
                                    </tr>
                                    <tr>
                                        <td><strong>CLB 7</strong></td>
                                        <td><strong>68–75</strong></td>
                                        <td>CEC &amp; FSW Minimum Eligibility</td>
                                    </tr>
                                    <tr>
                                        <td><strong>CLB 6</strong></td>
                                        <td><strong>59–67</strong></td>
                                        <td>PNP Minimum Eligibility</td>
                                    </tr>
                                    <tr>
                                        <td><strong>CLB 5</strong></td>
                                        <td><strong>51–58</strong></td>
                                        <td>Federal Skilled Trades (FST)</td>
                                    </tr>
                                    <tr>
                                        <td><strong>CLB 4</strong></td>
                                        <td><strong>42–50</strong></td>
                                        <td>Canadian Citizenship Minimum</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default DashboardPTE;
